/** Atlas key resolution for things the map draws, and the player palette.
 *
 * Content ids and atlas keys are different namespaces (STATUS.md, "the
 * second-biggest gap"): the packer keys frames semantically (`obj.mine_gold`)
 * while a `MapObjectDef` carries a filesystem path. `content.mapObjects` is
 * still empty, so nothing authoritative exists to join them yet. What this
 * module does is resolve a def id the way the packer would have named it, and
 * fall back to a representative sprite for the object's `MapObjectKind` when
 * that misses - a placeholder that is the right *kind* of thing beats an
 * invisible object, and it is loud enough to notice.
 *
 * Pure: no GPU, no DOM.
 */

import type { MapObjectKind } from "@game/core";

// ---------------------------------------------------------------- player colour

/** The packer's colour names, in the order `objects.json` lists them. */
export const PLAYER_COLOURS = [
  "red", "blue", "tan", "green", "orange", "purple", "teal", "pink",
] as const;
export type PlayerColour = (typeof PLAYER_COLOURS)[number];

/**
 * Flag tints, matched to the baked owner variants so a tinted banner and a
 * baked one sit side by side without arguing. Values are deliberately a little
 * desaturated: a pure primary reads as a UI swatch, not as dyed cloth.
 */
export const PLAYER_TINTS: Readonly<Record<PlayerColour, number>> = Object.freeze({
  red: 0xc4372c,
  blue: 0x2f5bbf,
  tan: 0xc3a268,
  green: 0x3d8f45,
  orange: 0xdd8324,
  purple: 0x8850b8,
  teal: 0x2b9fa0,
  pink: 0xd06aa8,
});

export const isPlayerColour = (s: string): s is PlayerColour =>
  (PLAYER_COLOURS as readonly string[]).includes(s);

export const playerTint = (colour: string | null): number =>
  colour !== null && isPlayerColour(colour) ? PLAYER_TINTS[colour] : 0x9aa3ad;

// ---------------------------------------------------------------- object keys

const KIND_FALLBACK: Readonly<Record<MapObjectKind, string>> = Object.freeze({
  resource: "obj.res_gold",
  mine: "obj.mine_gold",
  dwelling: "obj.dwl_hut",
  artifact: "obj.pu_artifact",
  monster: "obj.mon_bandit",
  treasure: "obj.pu_chest",
  scholar: "obj.bld_hermit_hut",
  shrine: "obj.bld_shrine",
  boat: "obj.boat_raft",
  garrison: "obj.tp_garrison",
  portal: "obj.tp_monolith",
  bank: "obj.bank_crypt",
  bonus: "obj.bld_lucky_totem",
  obstacle: "obj.obs_boulder",
  sign: "obj.obs_signpost",
  town: "obj.town_sunspire",
  hero: "obj.hero_vanguard",
  keymaster: "obj.tp_keymaster_tent",
  gate: "obj.tp_border_gate",
});

/** Candidate atlas keys for a def id, best first. */
export function objectKeyCandidates(def: string, kind: MapObjectKind): string[] {
  const bare = def.startsWith("obj.") ? def.slice(4) : def;
  const flat = bare.replace(/\./g, "_");
  const out = [`obj.${bare}`];
  if (flat !== bare) out.push(`obj.${flat}`);
  // `mine.gold` and `gold_mine` are both plausible authorings of the same thing.
  const last = flat.includes("_") ? flat.slice(flat.lastIndexOf("_") + 1) : flat;
  const prefix = KIND_FALLBACK[kind].slice(4).split("_")[0] ?? "";
  if (prefix !== "") out.push(`obj.${prefix}_${last}`);
  out.push(KIND_FALLBACK[kind]);
  return out;
}

/** The owner-flagged variant of a key, if the packer baked one. */
export const ownerVariantKey = (key: string, colour: string | null): string | null =>
  colour !== null && isPlayerColour(colour) ? `${key}.owner.${colour}` : null;

export const townObjectKey = (faction: string): string => `obj.town_${faction}`;

// ---------------------------------------------------------------- heroes

/**
 * The six adventure-map hero sprites the art set ships. A hero's faction picks
 * the closest of them; there is no per-hero adventure sprite in the classic either.
 */
export const HERO_SPRITE_BY_FACTION: Readonly<Record<string, string>> = Object.freeze({
  sunspire: "obj.hero_vanguard",
  greenmantle: "obj.hero_warden",
  skyreach: "obj.hero_magister",
  emberpit: "obj.hero_shadowcaller",
  gravewatch: "obj.hero_bonecaller",
  deepvault: "obj.hero_shadowcaller",
  ironhorde: "obj.hero_reaver",
  fenmire: "obj.hero_reaver",
  prismgate: "obj.hero_magister",
  saltreach: "obj.hero_vanguard",
  cogworks: "obj.hero_magister",
  frostmark: "obj.hero_reaver",
  blackwater: "obj.hero_wader",
});

export const heroSpriteKey = (faction: string): string =>
  HERO_SPRITE_BY_FACTION[faction] ?? "obj.hero_vanguard";

/** `HeroDefId` is `<faction>_<name>`, and the faction picks the map sprite. */
export const factionOfHeroDef = (def: string): string => {
  const i = def.indexOf("_");
  return i < 0 ? def : def.slice(0, i);
};

/**
 * Facing index 0..7 clockwise from north, matching `HeroState.facing` and
 * `NEIGHBOUR_DX`/`NEIGHBOUR_DY` in the kernel. Used only while a walk is being
 * interpolated: between two cells the observation's facing is stale, because
 * the kernel already wrote the facing of the *last* step.
 */
export function facingFromDelta(dx: number, dy: number): number {
  if (dx === 0 && dy === 0) return 4;
  if (dx === 0) return dy < 0 ? 0 : 4;
  if (dy === 0) return dx > 0 ? 2 : 6;
  if (dx > 0) return dy < 0 ? 1 : 3;
  return dy < 0 ? 7 : 5;
}

/**
 * `HeroState.facing` is 0..7 clockwise from north. The art ships one baked
 * three-quarter view plus a `facing.left` mirror with the key light re-baked,
 * so the eight headings collapse onto two sprites rather than being flipped in
 * code - flipping would put the highlight on the shadowed side.
 *
 * North and south keep the right-facing sprite, which is what the classic does.
 */
export const facesLeft = (facing: number): boolean => {
  const f = ((facing % 8) + 8) % 8;
  return f === 5 || f === 6 || f === 7;
};

export const directionalKey = (base: string, facing: number): string =>
  facesLeft(facing) ? `${base}.facing.left` : base;

/** Idle cycle: the resting frame plus `idle.1..3`. */
export const IDLE_FRAME_COUNT = 4;

export const idleKey = (base: string, frame: number): string => {
  const i = ((frame % IDLE_FRAME_COUNT) + IDLE_FRAME_COUNT) % IDLE_FRAME_COUNT;
  return i === 0 ? base : `${base}.idle.${i}`;
};
